import { useState, useEffect } from "react";
import {
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Box,
} from "@mui/material";
import { FaClock } from "react-icons/fa";
import { getAllWorkTypes } from "../../Services/workTypeService";
import { getAllCompensationTypes } from "../../Services/compensationService";
import { getAllUser } from "../../Services/authService";
import { getProjectByUserId } from "../../Services/projectService";
import { getApprovalFormByProjectId } from "../../Services/approvalFormService";
import {
  createTimeEntry,
  updateTimeEntry,
  getUserTimeEntries,
  approveTimeEntries,
} from "../../Services/timeEntryService";
import { toast } from "react-toastify";

import TimeEntriesTable from "./TimeEntriesTable";
import TimeEntryFilters from "./TimeEntryFilters";
import ActionButtons from "./ActionButtons";
import TimeEntryFormDialog from "./TimeEntryFormDialog";
import { formatDate, getWeekDates } from "./TimeListHelpers";

export default function TimeListAdmin() {
  const [currentWeek, setCurrentWeek] = useState(getWeekDates(new Date()));
  const [timeEntries, setTimeEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [scaffoldIds, setScaffoldIds] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [compensationTypes, setCompensationTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [inlineEditing, setInlineEditing] = useState(null);


  const [loading, setLoading] = useState({
    users: false,
    projects: false,
    scaffolds: false,
    workTypes: false,
    compensationTypes: false,
    timeEntries: false,
    saveEntry: false,
    approveEntries: false,
  });

  const [filters, setFilters] = useState({
    name: "",
    projectId: "",
    approvalStatus: "",
    searchTerm: "",
  });

  const getAllUsersData = async () => {
    try {
      setLoading((prev) => ({ ...prev, users: true }));
      const responseData = await getAllUser();
      if (responseData?.data) {
        setUsers(responseData.data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users", "error");
    } finally {
      setLoading((prev) => ({ ...prev, users: false }));
    }
  };

  const getAllProjects = async (userId) => {
    try {
      if (!userId) return;
      setLoading((prev) => ({ ...prev, projects: true }));
      const responseData = await getProjectByUserId(userId);
      if (responseData?.status) {
        setProjects(responseData?.data);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects", "error");
    } finally {
      setLoading((prev) => ({ ...prev, projects: false }));
    }
  };

  const getAllScaffoldIds = async (projectId) => {
    try {
      if (!projectId) return;
      setLoading((prev) => ({ ...prev, scaffolds: true }));
      const responseData = await getApprovalFormByProjectId(projectId);
      if (responseData?.success) {
        setScaffoldIds(responseData.data);
      }
    } catch (error) {
      console.error("Error fetching scaffold IDs:", error);
      toast.error("Failed to load scaffold IDs", "error");
    } finally {
      setLoading((prev) => ({ ...prev, scaffolds: false }));
    }
  };

  const getAllWorkTypesData = async () => {
    try {
      setLoading((prev) => ({ ...prev, workTypes: true }));
      const responseData = await getAllWorkTypes();
      if (responseData?.data) {
        setWorkTypes(responseData.data);
      }
    } catch (error) {
      console.error("Error fetching work types:", error);
      toast.error("Failed to load work types", "error");
    } finally {
      setLoading((prev) => ({ ...prev, workTypes: false }));
    }
  };

  const getAllCompensationTypesData = async () => {
    try {
      setLoading((prev) => ({ ...prev, compensationTypes: true }));
      const responseData = await getAllCompensationTypes();
      if (responseData?.data) {
        setCompensationTypes(responseData.data);
      }
    } catch (error) {
      console.error("Error fetching compensation types:", error);
      toast.error("Failed to load compensation types", "error");
    } finally {
      setLoading((prev) => ({ ...prev, compensationTypes: false }));
    }
  };

  const fetchAllTimeEntries = async () => {
    try {
      setLoading((prev) => ({ ...prev, timeEntries: true }));
      const response = await getUserTimeEntries();
      if (response?.success) {
        setTimeEntries(response.data);
        setFilteredEntries(response.data);
      }
    } catch (error) {
      console.error("Error fetching time entries:", error);
      toast.error("Failed to load time entries", "error");
    } finally {
      setLoading((prev) => ({ ...prev, timeEntries: false }));
    }
  };

  useEffect(() => {
    getAllUsersData();
    getAllWorkTypesData();
    getAllCompensationTypesData();
    fetchAllTimeEntries();
  }, []);

  useEffect(() => {
    let result = timeEntries;

    if (filters.searchTerm && filters.searchTerm.trim() !== "") {
      const searchTermLower = filters.searchTerm.toLowerCase().trim();
      result = result.filter((entry) => {
        return (
          (entry?.userId?.name &&
            entry.userId.name.toLowerCase().includes(searchTermLower)) ||
          (entry?.projectId?.projectName &&
            entry.projectId.projectName
              .toLowerCase()
              .includes(searchTermLower)) ||
          (entry?.scaffoldId?.scaffoldId &&
            entry.scaffoldId.scaffoldId
              .toLowerCase()
              .includes(searchTermLower)) ||
          (entry?.workTypeId?.name &&
            entry.workTypeId.name.toLowerCase().includes(searchTermLower)) ||
          (entry?.compensationTypeId?.name &&
            entry.compensationTypeId.name
              .toLowerCase()
              .includes(searchTermLower)) ||
          (entry?.comments &&
            entry.comments.toLowerCase().includes(searchTermLower)) ||
          (entry?.date &&
            formatDate(new Date(entry.date))
              .toLowerCase()
              .includes(searchTermLower))
        );
      });
    }

    if (filters.name && filters.name !== "all_users") {
      result = result.filter((entry) => entry?.userId?.name === filters.name);
    }

    if (filters.projectId && filters.projectId !== "all_projects") {
      result = result.filter((entry) => {
        const entryProjectId =
          typeof entry.projectId === "object"
            ? entry.projectId._id
            : entry.projectId;
        return entryProjectId === filters.projectId;
      });
    }

    if (filters.approvalStatus && filters.approvalStatus !== "all_statuses") {
      result = result.filter(
        (entry) => entry.approvalStatus === filters.approvalStatus
      );
    }

    setFilteredEntries(result);
  }, [filters, timeEntries]);

  const navigateWeek = (direction) => {
    const firstDay = new Date(currentWeek[0]);
    firstDay.setDate(firstDay.getDate() + (direction === "next" ? 7 : -7));
    setCurrentWeek(getWeekDates(firstDay));
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedEntries(filteredEntries?.map((entry) => entry._id));
    } else {
      setSelectedEntries([]);
    }
  };

  const handleSelectEntry = (entryId, checked) => {
    if (checked) {
      setSelectedEntries([...selectedEntries, entryId]);
    } else {
      setSelectedEntries(selectedEntries.filter((id) => id !== entryId));
    }
  };

  const approveSelected = async () => {
    if (selectedEntries.length === 0) return;

    try {
      setLoading((prev) => ({ ...prev, approveEntries: true }));
      const response = await approveTimeEntries(selectedEntries);

      if (response?.success) {
        fetchAllTimeEntries();
        setSelectedEntries([]);
        toast.success(
          `Successfully approved ${selectedEntries.length} entries`
        );
      } else {
        toast.error(
          `Failed to approve entries: ${response?.message || "Unknown error"}`
        );
      }
    } catch (error) {
      console.error("Error approving entries:", error);
      toast.error("Failed to approve some entries");
    } finally {
      setLoading((prev) => ({ ...prev, approveEntries: false }));
    }
  };

  const exportData = (format) => {
    toast.success(`Exporting data as ${format.toUpperCase()}`);
  };

  const validateForm = () => {
    const errors = {};

    if (!editingEntry.userId) {
      errors.userId = "User is required";
    }

    if (!editingEntry.projectId) {
      errors.projectId = "Project is required";
    }

    if (!editingEntry.scaffoldId) {
      errors.scaffoldId = "Scaffold ID is required";
    }

    if (!editingEntry.workTypeId) {
      errors.workTypeId = "Work type is required";
    }

    if (!editingEntry.date) {
      errors.date = "Date is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveEditedEntry = async () => {
    if (!editingEntry) return;

    if (!validateForm()) {
      toast.error("Please fill in all required fields", "error");
      return;
    }

    const updatedEntry = {
      ...editingEntry,
    };

    try {
      setLoading((prev) => ({ ...prev, saveEntry: true }));

      if (updatedEntry.id?.startsWith("new")) {
        delete updatedEntry.id;
        const response = await createTimeEntry(updatedEntry);
        if (response.success) {
          const project = projects.find(
            (p) => p._id === updatedEntry.projectId
          );
          const scaffold = scaffoldIds.find(
            (s) => s._id === updatedEntry.scaffoldId
          );
          const workType = workTypes.find(
            (w) => w._id === updatedEntry.workTypeId
          );
          const user = users.find((u) => u._id === updatedEntry.userId);

          const completedEntry = {
            ...response.data,
            projectId: {
              _id: updatedEntry.projectId,
              projectName: project?.projectName || "Unknown",
            },
            scaffoldId: {
              _id: updatedEntry.scaffoldId,
              scaffoldId: scaffold?.scaffolderowner || "Unknown",
            },
            workTypeId: {
              _id: updatedEntry.workTypeId,
              name: workType?.name || "Unknown",
            },
            userId: { _id: updatedEntry.userId, name: user?.name || "Unknown" },
          };

          setTimeEntries([...timeEntries, completedEntry]);
          toast.success("Time entry created successfully");
        } else {
          toast.error(response.message || "Failed to create entry", "error");
        }
      } else {
        const response = await updateTimeEntry(updatedEntry._id, updatedEntry);
        if (response.success) {
          const updatedEntries = timeEntries?.map((entry) => {
            if (entry._id === updatedEntry._id) {
              return {
                ...updatedEntry,
                projectId:
                  typeof updatedEntry.projectId === "object"
                    ? updatedEntry.projectId
                    : {
                        _id: updatedEntry.projectId,
                        projectName:
                          projects.find((p) => p._id === updatedEntry.projectId)
                            ?.projectName || "Unknown",
                      },
                scaffoldId:
                  typeof updatedEntry.scaffoldId === "object"
                    ? updatedEntry.scaffoldId
                    : {
                        _id: updatedEntry.scaffoldId,
                        scaffoldId:
                          scaffoldIds.find(
                            (s) => s._id === updatedEntry.scaffoldId
                          )?.scaffolderowner || "Unknown",
                      },
                workTypeId:
                  typeof updatedEntry.workTypeId === "object"
                    ? updatedEntry.workTypeId
                    : {
                        _id: updatedEntry.workTypeId,
                        name:
                          workTypes.find(
                            (w) => w._id === updatedEntry.workTypeId
                          )?.name || "Unknown",
                      },
                userId:
                  typeof updatedEntry.userId === "object"
                    ? updatedEntry.userId
                    : {
                        _id: updatedEntry.userId,
                        name:
                          users.find((u) => u._id === updatedEntry.userId)
                            ?.name || "Unknown",
                      },
              };
            }
            return entry;
          });
          setTimeEntries(updatedEntries);
          toast.success("Time entry updated successfully");
        } else {
          toast.error(response.message || "Failed to update entry", "error");
        }
      }
      setEditingEntry(null);
    } catch (error) {
      console.error("Error saving entry:", error);
      toast.error("Failed to save entry", "error");
    } finally {
      setLoading((prev) => ({ ...prev, saveEntry: false }));
    }
  };

  const addNewEntry = () => {
    const newEntry = {
      id: `new-${Date.now()}`,
      name: "",
      userId: "",
      projectId: "",
      projectName: "",
      scaffoldId: "",
      scaffoldName: "",
      date: new Date().toISOString().split("T")[0],
      workTypeId: "",
      workTypeName: "",
      compensationTypeId: null,
      compensationTypeName: null,
      comments: "",
      approvalStatus: "pending",
    };

    setEditingEntry(newEntry);
    setFormErrors({});
  };

  const handleUserChange = (userId, userName) => {
    if (Array.isArray(userId)) {
      setSelectedUsers(userId);
      if (userId.length > 0) {
        const firstUser = users.find((u) => u._id === userId[0]);
        setEditingEntry({
          ...editingEntry,
          userId: userId[0],
          name: firstUser?.name || "",
          projectId: "",
          projectName: "",
          scaffoldId: "",
          scaffoldName: "",
        });
        getAllProjects(userId[0]);
      }
    } else {
      setEditingEntry({
        ...editingEntry,
        userId: userId,
        name: userName,
        projectId: "",
        projectName: "",
        scaffoldId: "",
        scaffoldName: "",
      });
      setFormErrors({ ...formErrors, userId: null });
      getAllProjects(userId);
    }
  };

  const handleProjectChange = (projectId, projectName) => {
    setEditingEntry({
      ...editingEntry,
      projectId: projectId,
      projectName: projectName,
      scaffoldId: "",
      scaffoldName: "",
    });
    setFormErrors({ ...formErrors, projectId: null });

    getAllScaffoldIds(projectId);
  };

  const approveEntry = async (entryId) => {
    try {
      const entryIdsInArray = [entryId];
      const updatedEntries = await approveTimeEntries(entryIdsInArray);
      if (updatedEntries?.success) {
        fetchAllTimeEntries();
        toast.success("Entry approved successfully");
      }
    } catch (error) {
      console.error("Error approving entry:", error);
      toast.error("Failed to approve entry", "error");
    }
  };

  const startInlineEdit = (entry, field) => {
    if (entry.approvalStatus === "approved") {
      toast.warning("Cannot edit approved entries");
      return;
    }

    setInlineEditing({ entryId: entry._id, field });

    if (field === "scaffoldId" && entry.projectId) {
      const projectId =
        typeof entry.projectId === "object"
          ? entry.projectId._id
          : entry.projectId;
      getAllScaffoldIds(projectId);
    }

    if (field === "projectId" && entry.userId) {
      const userId =
        typeof entry.userId === "object" ? entry.userId._id : entry.userId;
      getAllProjects(userId);
    }
  };

  const handleInlineChange = (entry, field, value) => {
    const updatedEntries = timeEntries?.map((e) => {
      if (e._id === entry._id) {
        let updatedEntry = { ...e };

        if (field === "projectId") {
          const project = projects.find((p) => p._id === value);
          updatedEntry.projectId = {
            _id: value,
            projectName: project?.projectName || "Unknown",
          };
          updatedEntry.scaffoldId = { _id: "", scaffoldId: "" };
        } else if (field === "scaffoldId") {
          const scaffold = scaffoldIds.find((s) => s._id === value);
          updatedEntry.scaffoldId = {
            _id: value,
            scaffoldId: scaffold?.scaffolderowner || "Unknown",
          };
        } else {
          updatedEntry[field] = value;
        }

        return updatedEntry;
      }
      return e;
    });

    setTimeEntries(updatedEntries);
  };

  const saveInlineEdit = async (entry) => {
    try {
      const entryToUpdate = timeEntries.find((e) => e._id === entry._id);
      if (!entryToUpdate) return;

      const updateData = {
        ...entryToUpdate,
        projectId:
          typeof entryToUpdate.projectId === "object"
            ? entryToUpdate.projectId._id
            : entryToUpdate.projectId,
        scaffoldId:
          typeof entryToUpdate.scaffoldId === "object"
            ? entryToUpdate.scaffoldId._id
            : entryToUpdate.scaffoldId,
        workTypeId:
          typeof entryToUpdate.workTypeId === "object"
            ? entryToUpdate.workTypeId._id
            : entryToUpdate.workTypeId,
        userId:
          typeof entryToUpdate.userId === "object"
            ? entryToUpdate.userId._id
            : entryToUpdate.userId,
        compensationTypeId: entryToUpdate.compensationTypeId
          ? typeof entryToUpdate.compensationTypeId === "object"
            ? entryToUpdate.compensationTypeId._id
            : entryToUpdate.compensationTypeId
          : null,
      };

      const response = await updateTimeEntry(entry._id, updateData);

      if (response.success) {
        toast.success("Entry updated successfully");
      } else {
        toast.error(response.message || "Failed to update entry");
        fetchAllTimeEntries();
      }
    } catch (error) {
      console.error("Error saving inline edit:", error);
      toast.error("Failed to save changes");
      fetchAllTimeEntries();
    } finally {
      setInlineEditing(null);
    }
  };

  const cancelInlineEdit = () => {
    setInlineEditing(null);
    fetchAllTimeEntries();
  };

  const createMultipleEntries = async () => {
    if (!editingEntry || !selectedUsers.length) return;

    if (!validateForm()) {
      toast.error("Please fill in all required fields", "error");
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, saveEntry: true }));
      const successEntries = [];
      const errors = [];
      await Promise.all(
        selectedUsers?.map(async (userId) => {
          const user = users.find((u) => u._id === userId);
          const entryData = {
            ...editingEntry,
            userId: userId,
            name: user?.name || "",
          };

          if (entryData.id?.startsWith("new")) {
            delete entryData.id;
          }

          try {
            const response = await createTimeEntry(entryData);
            if (response.success) {
              successEntries.push(response.data);
            } else {
              errors.push(
                `Failed for ${user?.name}: ${
                  response.message || "Unknown error"
                }`
              );
            }
          } catch (error) {
            errors.push(
              `Failed for ${user?.name}: ${error.message || "Unknown error"}`
            );
          }
        })
      );

      if (successEntries.length > 0) {
        const newEntries = successEntries?.map((entry) => {
          const project = projects.find((p) => p._id === entry.projectId);
          const scaffold = scaffoldIds.find((s) => s._id === entry.scaffoldId);
          const workType = workTypes.find((w) => w._id === entry.workTypeId);
          const user = users.find((u) => u._id === entry.userId);

          return {
            ...entry,
            projectId: {
              _id: entry.projectId,
              projectName: project?.projectName || "Unknown",
            },
            scaffoldId: {
              _id: entry.scaffoldId,
              scaffoldId: scaffold?.scaffolderowner || "Unknown",
            },
            workTypeId: {
              _id: entry.workTypeId,
              name: workType?.name || "Unknown",
            },
            userId: { _id: entry.userId, name: user?.name || "Unknown" },
          };
        });

        setTimeEntries([...timeEntries, ...newEntries]);
        toast.success(`Created ${successEntries.length} entries successfully`);
      }

      if (errors.length > 0) {
        errors.forEach((error) => toast.error(error));
      }

      if (successEntries.length > 0) {
        setEditingEntry(null);
        setSelectedUsers([]);
      }
    } catch (error) {
      console.error("Error creating multiple entries:", error);
      toast.error("Failed to create entries", "error");
    } finally {
      setLoading((prev) => ({ ...prev, saveEntry: false }));
    }
  };

  const calculateTotalHours = () => {
    return filteredEntries
      .reduce((total, entry) => {
        const hours = parseFloat(entry.totalHours);
        return total + (isNaN(hours) ? 0 : hours);
      }, 0)
      .toFixed(2); 
  };

  return (
    <div style={{ marginTop: "20px" }}>
      <Card>
        <CardHeader
          title={
            <Typography
              variant="h5"
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <FaClock style={{ color: "#1976d2" }} />
              Time Entries Management
            </Typography>
          }
          action={
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Button variant="outlined" onClick={() => navigateWeek("prev")}>
                Previous Week
              </Button>
              <Typography variant="body1">
                {formatDate(currentWeek[0])} - {formatDate(currentWeek[6])}
              </Typography>
              <Button variant="outlined" onClick={() => navigateWeek("next")}>
                Next Week
              </Button>
            </div>
          }
        />
        <CardContent>
          <TimeEntryFilters
            filters={filters}
            setFilters={setFilters}
            users={users}
            projects={projects}
            loading={loading}
          />

          <ActionButtons
            selectedEntries={selectedEntries}
            loading={loading}
            approveSelected={approveSelected}
            addNewEntry={addNewEntry}
            exportData={exportData}
          />

          <TimeEntriesTable
            loading={loading.timeEntries}
            filteredEntries={filteredEntries}
            selectedEntries={selectedEntries}
            handleSelectAll={handleSelectAll}
            handleSelectEntry={handleSelectEntry}
            approveEntry={approveEntry}
            setEditingEntry={setEditingEntry}
            startInlineEdit={startInlineEdit}
            handleInlineChange={handleInlineChange}
            saveInlineEdit={saveInlineEdit}
            cancelInlineEdit={cancelInlineEdit}
            inlineEditing={inlineEditing}
            projects={projects}
            scaffoldIds={scaffoldIds}
            searchTerm={filters.searchTerm}
            totalHours={calculateTotalHours()}
          />
          {filteredEntries.length > 0 && (
            <Box sx={{ mb: 2, mt: 2 }}>
              <Typography variant="body2">
                {filteredEntries.length}{" "}
                {filteredEntries.length === 1 ? "entry" : "entries"} found
                {filters.searchTerm ? ` matching "${filters.searchTerm}"` : ""}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {editingEntry && (
        <TimeEntryFormDialog
          editingEntry={editingEntry}
          setEditingEntry={setEditingEntry}
          loading={loading}
          users={users}
          projects={projects}
          scaffoldIds={scaffoldIds}
          workTypes={workTypes}
          compensationTypes={compensationTypes}
          formErrors={formErrors}
          selectedUsers={selectedUsers}
          handleUserChange={handleUserChange}
          handleProjectChange={handleProjectChange}
          saveEditedEntry={saveEditedEntry}
          createMultipleEntries={createMultipleEntries}
        />
      )}
    </div>
  );
}
